import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Mail, Paperclip, Send, AlertCircle } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { API_BASE } from '@/lib/api';

export default function AdminGroupEmailTab() {
  const { toast } = useToast();
  const { getToken } = useAuth();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isTestMode, setIsTestMode] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast({
        title: "Missing fields",
        description: "Please provide a subject and a body for the email.",
        variant: "destructive",
      });
      return;
    }

    if (!isTestMode && !confirm("WARNING: You are about to send this email to EVERY user. Are you sure you want to proceed?")) {
      return;
    }

    setIsSending(true);

    try {
      const token = await getToken();
      if (!token) throw new Error("Authentication required");

      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('testMode', String(isTestMode));

      files.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE}/admin-group-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email');
      }

      toast({
        title: isTestMode ? "Test Email Sent" : "Group Emails Sent",
        description: `Successfully sent ${result.sentCount || 1} emails. ${result.failedCount ? `(${result.failedCount} failed)` : ''}`,
      });

      // Clear form after successful send
      setSubject('');
      setBody('');
      setFiles([]);

    } catch (error: any) {
      console.error("Error sending group email:", error);
      toast({
        title: "Failed to send emails",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card className="border-slate-700 bg-slate-900 w-full">
      <CardHeader>
        <CardTitle className="text-xl text-white flex items-center gap-2">
          <Mail className="h-5 w-5 text-indigo-400" />
          Send Group Email
        </CardTitle>
        <CardDescription className="text-slate-400">
          Compose and send emails with attachments to all registered users.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="subject" className="text-white">Subject</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Important Update regarding HiPo Services"
              className="border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body" className="text-white">Email Body (HTML Supported)</Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type your email content here. You can use HTML tags like <b>bold</b>, <br> for new lines, etc."
              className="min-h-[250px] border-slate-700 bg-slate-950 text-white placeholder:text-slate-500 font-mono text-sm"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="attachments" className="text-white block">Attachments</Label>
            <div className="flex items-center gap-4">
              <Button type="button" className="bg-royal-blue !text-white relative cursor-pointer">
                <Paperclip className="mr-2 h-4 w-4" />
                <span className='text-white'>{files.length > 0 ? `${files.length} file(s) selected` : 'Select Files'}</span>
                <Input
                  id="attachments"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </Button>
              {files.length > 0 && (
                <Button type="button" className="bg-royal-blue !text-white" onClick={() => setFiles([])}>
                  Clear files
                </Button>
              )}
            </div>
            {files.length > 0 && (
              <div className="mt-2 text-sm text-slate-400 flex flex-col gap-1">
                {files.map((file, i) => (
                  <span key={i} className="flex items-center gap-1 !text-white">
                    <div className="w-1 h-1 bg-indigo-400 rounded-full" />
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg bg-slate-950 border border-slate-800">
            <div className="space-y-0.5">
              <Label htmlFor="test-mode" className="text-white font-semibold">Test Mode</Label>
              <p className="text-sm text-slate-400">
                When enabled, the email will only be sent to your admin email address.
              </p>
            </div>
            <div className="flex items-center h-5">
              <Input
                id="test-mode"
                type="checkbox"
                checked={isTestMode}
                onChange={(e) => setIsTestMode(e.target.checked)}
                className="w-5 h-5 accent-indigo-500"
              />
            </div>
          </div>

          {!isTestMode && (
            <div className="p-4 rounded-lg bg-red-950/30 border border-red-900/50 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-200">
                <strong className="block text-red-400 font-semibold mb-1">DANGER: Test Mode Disabled</strong>
                Clicking send will dispatch this email to the entire user database. This action cannot be undone. Please double check your content and attachments.
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={isSending || !subject.trim() || !body.trim()}
            className={`w-full font-semibold px-4 py-6 !text-white text-md ${isTestMode
              ? 'bg-indigo-600 hover:bg-indigo-700'
              : 'bg-red-600 hover:bg-red-700 shadow-[0_0_15px_rgba(220,38,38,0.5)]'
              }`}
          >
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin !text-white" />
                Sending Emails...
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5 !text-white" />
                {isTestMode ? 'Send Test Email' : 'SEND TO ALL USERS'}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
